import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const JSHigherOrder = () => {
    const declarativeVsImperativeCode = `//  Imperative: "How" to do it (Manual iteration)
const numbers = [1, 2, 3];
const doubledImperative = [];
for (let i = 0; i < numbers.length; i++) {
    doubledImperative.push(numbers[i] * 2);
}

// Declarative: "What" to do (Using HOF)
const doubledDeclarative = numbers.map(n => n * 2);`;

    const transformationPipelineCode = `const inventory = [
    { name: 'Laptop', price: 1200, category: 'Electronics' },
    { name: 'Desk', price: 150, category: 'Furniture' },
    { name: 'Mouse', price: 25, category: 'Electronics' }
];

// Production Pipeline: Filter -&gt; Map -&gt; Sort
const premiumElectronics = inventory
    .filter(item => item.category === 'Electronics' && item.price &gt; 100)
    .map(item => item.name.toUpperCase())
    .sort();

console.log(premiumElectronics); // ['LAPTOP']`;

    const middlewarePatternCode = `// HOF for Authentication Middleware (Conceptual)
const withAuth = (handler) => {
    return (req, res) => {
        if (!req.isAuthenticated) {
            return res.status(401).send('Unauthorized');
        }
        return handler(req, res);
    };
};

// Usage
const deleteUser = (req, res) => { /* Logic */ };
const protectedDelete = withAuth(deleteUser);`;

    const compositionCode = `// Functional Composition (Pipe Pattern)
const pipe = (...fns) => (x) => fns.reduce((v, f) => f(v), x);

const trim = (s) => s.trim();
const capitalize = (s) => s.toUpperCase();
const exclaim = (s) => \`\${s}!\`;

const prepareHeadline = pipe(trim, capitalize, exclaim);

console.log(prepareHeadline('  breaking news  ')); // 'BREAKING NEWS!'`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Functional Paradigm</div>
                <h1>Higher-Order Functions</h1>
                <p className="hero-lead">
                    Higher-Order Functions (HOFs) represent a shift from <strong>Imperative</strong>
                    instruction to <strong>Declarative</strong> expression. They are the
                    essential building blocks for scalable, predictable JavaScript architecture.
                </p>
            </div>

            <div className="content-section">
                <h2>The Paradigm Shift</h2>
                <p>
                    A Higher-Order Function is simply a function that either accepts
                    another function as an argument or returns one. This allows us to
                    abstract <strong>behavior</strong>, not just values.
                </p>
                <CodeBlock language="javascript" code={declarativeVsImperativeCode} />
                <div className="key-point">
                    Building "What" instead of "How" reduces the surface area for bugs
                    (like off-by-one errors in loops) and makes code self-documenting.
                </div>
            </div>

            <div className="content-section">
                <h2>Data Transformation Pipelines</h2>
                <p>
                    In modern frontend development (React, Vue), 90% of data handling involves
                    the "Big Three": <code>map</code>, <code>filter</code>, and <code>reduce</code>.
                    Chaining these creates readable, linear data flows.
                </p>
                <CodeBlock language="javascript" code={transformationPipelineCode} />
                <ProTip><strong> Performance Note:</strong> Chaining creates intermediate
                    arrays. For extremely large datasets (100k+ items), consider
                    using a single <code>reduce</code> or a <strong>transducer</strong>
                    to avoid the "Abstraction Tax" of multiple iterations.</ProTip>
            </div>

            <div className="content-section">
                <h2>HOFs as Middleware</h2>
                <p>
                    HOFs are not just for arrays. In server-side JavaScript (Node.js) and
                    Next.js API routes, HOFs are used to "wrap" functions with logic like
                    <strong>Authentication</strong>, <strong>Logging</strong>, or
                    <strong>Error Handling</strong>.
                </p>
                <CodeBlock language="javascript" code={middlewarePatternCode} />
            </div>

            <div className="content-section">
                <h2>Functional Composition</h2>
                <p>
                    Advanced HOF patterns like <code>pipe</code> and <code>compose</code> allow
                    you to build complex logic by combining small, pure, single-purpose
                    functions. This is the hallmark of <strong>Clean Code</strong>.
                </p>
                <CodeBlock language="javascript" code={compositionCode} />
            </div>

            <KeyFacts><ul>
                    <li><strong>Dry Code:</strong> Use HOFs to extract repetitive logic (e.g., retries, timing).</li>
                    <li><strong>Immutability:</strong> Prefer <code>map/filter</code> over <code>forEach</code> to avoid side effects.</li>
                    <li><strong>Predictability:</strong> Pure HOFs are easier to unit test than complex imperative loops.</li>
                    <li> <strong>Performance Audits:</strong> Be wary of deep chains on massive, performance-critical datasets.</li>
                    <li><strong>Clean APIs:</strong> Use factories to generate specialized functions from generic ones.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSHigherOrder;



